import { useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Button, Dialog } from 'vnetwork-ui'
import { AsyncFilterSelect } from '@/components/ui/filters/async-filter-select'
import { DataTableCustomized } from '@/components/ui/data-table-customized'
import type { DataTableColumn } from '@/components/ui/data-table-customized'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { bulkAssignCustomerCS } from '@/modules/Customer/services'
import {
    getAccountManagerOptionsPaginated,
    getEmployeesDefaultOptions,
} from '@/modules/Employee/services'
import type { CustomerActive } from '@/modules/Customer/types'
import { ModalProps } from '@/types/shared'
import { generatePathDatacentral, paths } from '@/utils/path'
import { Link } from 'react-router-dom'

export interface BulkAssignCSModalProps extends ModalProps {
    customers: CustomerActive[]
    onSuccess?: () => void
}

const BulkAssignCSModal: React.FC<BulkAssignCSModalProps> = ({
    onClose,
    open,
    customers,
    onSuccess,
}) => {
    const { t } = useTranslation('customer')
    const queryClient = useQueryClient()

    const { control, watch, reset } = useForm({
        defaultValues: {
            employee_ids: [] as number[],
        },
    })

    const employeeIds = watch('employee_ids')

    // Local pagination for preview table
    const [previewPage, setPreviewPage] = useState(1)
    const PAGE_SIZE = 5
    const paginatedCustomers = useMemo(
        () =>
            customers.slice(
                (previewPage - 1) * PAGE_SIZE,
                previewPage * PAGE_SIZE
            ),
        [customers, previewPage]
    )

    // Preview columns for selected customers
    const previewColumns = useMemo<DataTableColumn<CustomerActive>[]>(
        () => [
            {
                key: 'customer_name',
                title: t('customer_name'),
                width: 250,
                render: (_, row) => (
                    <Link
                        to={generatePathDatacentral(paths.customerDetail, {
                            id: row.customer_id,
                        })}
                        className="text-primary hover:underline uppercase"
                        target="_blank"
                    >
                        {row.customer_id} -{' '}
                        {row.company_name || row.customer_name}
                    </Link>
                ),
            },
            {
                key: 'customer_sale_followers',
                title: t('customer_sales_followers'),
                width: 200,
                render: (value) => {
                    const names = value
                        ?.map((item: any) => item?.employee_name)
                        ?.join(', ')
                    return (
                        <span className="truncate block" title={names || ''}>
                            {names || '-'}
                        </span>
                    )
                },
            },
            {
                key: 'customer_industry',
                title: t('customer_industry'),
                width: 150,
                render: (value) => (
                    <span className="capitalize">{value || '-'}</span>
                ),
            },
        ],
        [t]
    )

    const { mutateAsync: bulkAssignMutation, isPending } =
        useMutationWithHandler(
            { mutationFn: bulkAssignCustomerCS },
            {
                reactQueryKeys: QUERY_KEYS.CUSTOMERS,
                showSuccessToast: true,
                showErrorToast: true,
            }
        )

    const handleClose = () => {
        reset({ employee_ids: [] })
        onClose()
    }

    const handleAssign = async () => {
        if (employeeIds.length === 0 || customers.length === 0) return

        try {
            await bulkAssignMutation({
                customer_ids: customers.map((c) => c.customer_id),
                employee_ids: employeeIds,
            })

            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.CUSTOMERS, 'active'],
            })
            onSuccess?.()
            handleClose()
        } catch {
            // Error handled by useMutationWithHandler
        }
    }

    return (
        <Dialog
            onClose={handleClose}
            open={open}
            maxWidth="lg"
            title={t('bulk_assign_cs', { count: customers.length })}
            children={
                <div className="space-y-4">
                    <p className="text-sm text-muted-foreground">
                        {t('bulk_assign_cs_description', {
                            count: customers.length,
                        })}
                    </p>

                    {/* Preview selected customers */}
                    <div className="border rounded-md">
                        <DataTableCustomized<CustomerActive>
                            columns={previewColumns}
                            rows={paginatedCustomers}
                            rowKey={(row) => row.customer_id}
                            size="small"
                            pagination={{
                                page: previewPage,
                                pageSize: PAGE_SIZE,
                                total: customers.length,
                                onPageChange: setPreviewPage,
                                pageSizeOptions: [5],
                            }}
                        />
                    </div>

                    {/* CS follower selection — ACCOUNT department only */}
                    <AsyncFilterSelect
                        control={control}
                        name="employee_ids"
                        label={t('cs_followers')}
                        placeholder={t('select_cs_followers')}
                        asyncOptions={getAccountManagerOptionsPaginated}
                        getDefaultOptions={getEmployeesDefaultOptions}
                        multiple
                    />
                </div>
            }
            actions={
                <div className="flex items-center gap-2">
                    <Button
                        variant="outlined"
                        onClick={handleClose}
                        disabled={isPending}
                    >
                        {t('cancel', { ns: 'translation' })}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleAssign}
                        disabled={employeeIds.length === 0 || isPending}
                        loading={isPending}
                    >
                        {t('assign', { ns: 'translation' })}
                    </Button>
                </div>
            }
        />
    )
}

export default BulkAssignCSModal
